"use client";

import { X } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IconButton } from "@/components/ui/icon-button";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import {
  PRODUCT_CONFIGS,
  type TruckLoadingProductSlug,
} from "@/lib/calculators/truck-loading-merged/product-presets";
import {
  presetStackable,
  presetWeightLbs,
} from "@/lib/calculators/truck-loading-merged/preset-values";
import { Checkbox } from "@/components/ui/checkbox";
import type { AdditionalItem } from "./use-calculator-state";

const ADDITIONAL_ITEM_PRESETS = buildPresetOptions();

const PRESET_CHOICES = [
  { value: "custom", label: "Custom (enter dimensions)" },
  ...ADDITIONAL_ITEM_PRESETS.map(({ value, label }) => ({ value, label })),
];

function buildPresetOptions() {
  const options: Array<{
    value: string;
    label: string;
    productSlug: TruckLoadingProductSlug;
    lengthIn: number;
    widthIn: number;
    heightIn: number;
    weightLbs: number;
    stackable: boolean;
  }> = [];

  for (const productConfig of Object.values(PRODUCT_CONFIGS)) {
    for (const preset of productConfig.presets) {
      if (preset.id === "custom") continue;
      options.push({
        value: `${productConfig.slug}__${preset.id}`,
        label: `${productConfig.singularLabel} — ${preset.label}`,
        productSlug: productConfig.slug,
        lengthIn: preset.lengthIn,
        widthIn: preset.widthIn,
        heightIn: preset.heightIn,
        weightLbs: presetWeightLbs(productConfig, preset),
        stackable: presetStackable(productConfig, preset),
      });
    }
  }

  return options;
}

interface AdditionalItemRowProps {
  item: AdditionalItem;
  onUpdate: (id: string, updates: Partial<Omit<AdditionalItem, "id">>) => void;
  onRemove: (id: string) => void;
}

function handlePresetSelect(
  compositeValue: string,
  itemId: string,
  onUpdate: AdditionalItemRowProps["onUpdate"],
) {
  if (compositeValue === "custom") {
    onUpdate(itemId, { presetId: "custom", length: "", width: "", height: "" });
    return;
  }
  const preset = ADDITIONAL_ITEM_PRESETS.find(
    (candidate) => candidate.value === compositeValue,
  );
  if (preset) {
    onUpdate(itemId, {
      presetId: compositeValue,
      length: String(preset.lengthIn),
      width: String(preset.widthIn),
      height: String(preset.heightIn),
      weight: preset.weightLbs > 0 ? String(preset.weightLbs) : "",
      stackable: preset.stackable,
    });
  }
}

export function AdditionalItemRow({
  item,
  onUpdate,
  onRemove,
}: AdditionalItemRowProps) {
  return (
    <div className="bg-card border border-border p-3">
      <Label htmlFor={`${item.id}-preset`} size="sm" tone="muted">
        Product size
      </Label>
      <div className="mt-1 mb-2 flex items-center gap-2">
        <InlineCombobox
          id={`${item.id}-preset`}
          size="compact"
          value={item.presetId}
          options={PRESET_CHOICES}
          onSelect={(value) => handlePresetSelect(value, item.id, onUpdate)}
          placeholder="Select product size"
          searchPlaceholder="Search sizes"
        />
        <IconButton
          aria-label="Remove item"
          variant="destructive-quiet"
          size="compact"
          onClick={() => onRemove(item.id)}
        >
          <X />
        </IconButton>
      </div>
      <AdditionalItemDimensions item={item} onUpdate={onUpdate} />
      <div className="mt-2 flex items-center gap-2">
        <Checkbox
          id={`${item.id}-stackable`}
          checked={item.stackable}
          onCheckedChange={(checked) =>
            onUpdate(item.id, { stackable: checked === true })
          }
        />
        <Label htmlFor={`${item.id}-stackable`} size="sm" weight="normal">
          Stackable
        </Label>
      </div>
    </div>
  );
}

function AdditionalItemDimensions({
  item,
  onUpdate,
}: {
  item: AdditionalItem;
  onUpdate: AdditionalItemRowProps["onUpdate"];
}) {
  const dimensionFields = [
    { label: "L (in)", field: "length" as const, clearPreset: true },
    { label: "W (in)", field: "width" as const, clearPreset: true },
    { label: "H (in)", field: "height" as const, clearPreset: true },
    { label: "Wt (lbs)", field: "weight" as const, clearPreset: false },
    { label: "Qty", field: "quantity" as const, clearPreset: false },
  ];

  return (
    <div className="grid grid-cols-5 gap-2">
      {dimensionFields.map(({ label, field, clearPreset }) => (
        <div key={field}>
          <Label htmlFor={`${item.id}-${field}`} size="sm" tone="muted" weight="normal">
            {label}
          </Label>
          <Input
            id={`${item.id}-${field}`}
            type="number"
            min={field === "quantity" ? "1" : "0"}
            step="1"
            value={item[field]}
            onChange={(event) =>
              onUpdate(item.id, {
                [field]: event.target.value,
                ...(clearPreset ? { presetId: "" } : {}),
              })
            }
            variant="mono" size="compact"
          />
        </div>
      ))}
    </div>
  );
}
